import { useState, useEffect, useCallback } from 'react';
import api from 'services/api';
import useEscapeClose from 'hooks/useEscapeClose';
import { AUCUN_POSTE } from './libelles-referentiels';
import { useAuth } from 'context/AuthContext';
import { useTenant } from 'context/TenantContext';
import { canAction } from 'permissions';
import { formatDate } from '../../../../utils/jour';

/* ⚠️ LES ESPACES QU'UN HUMAIN OU UN PRESSE-PAPIER POSE DANS UN NOMBRE —
 *    écrites par leur CODE, jamais en caractères littéraux, et définies UNE
 *    SEULE FOIS. Payé deux fois le 12/10/2026 : d'abord quatre espaces
 *    ordinaires là où je croyais avoir tapé trois insécables (l'éditeur les
 *    avait normalisées, et rien ne se voyait), puis la même classe recopiée à
 *    deux endroits du fichier, dont un seul corrigé.
 * ⓘ Jumelle de `ESPACES` dans `backend/src/utils/montant.js`, qui fait foi.
 *   U+0020 ordinaire · U+00A0 insécable · U+202F insécable étroite · U+2009
 *   fine · tabulation. */
const ESPACES_MONTANT = /[\u0020\u00A0\u202F\u2009\t]/g;

const CONTRAT_COLORS = { CDI: '#2e7d32', CDD: '#1565c0', Periode_essai: '#f57c00', Interim: '#7b1fa2', Stage: '#9e9e9e' };
const CONTRAT_LABELS = { CDI: 'CDI', CDD: 'CDD', Periode_essai: "Periode d'essai", Interim: 'Interim', Stage: 'Stage' };
const STATUT_COLORS = { actif: '#2e7d32', en_conge: '#f57c00', suspendu: '#e53935', inactif: '#9e9e9e' };
const STATUT_LABELS = { actif: 'Actif', en_conge: 'En conge', suspendu: 'Suspendu', inactif: 'Inactif' };
// ⚠️ LA LISTE EN DUR A ETE RETIREE LE 05/09/2026. Elle portait six valeurs,
//    dont `maitre_chien` et `operateur_tsv` qui n'existaient dans AUCUNE table :
//    un agent cree ici pouvait porter un poste sans fiche ni referentiel. Et
//    aucun des cinq postes de VIGIE n'y figurait — ses agents ne pouvaient pas
//    se voir attribuer leur propre poste.
//    Les postes viennent desormais de `/postes`, le referentiel DU TENANT.
// ⚠️ `FORMATIONS_LIST` A ETE RETIREE LE 16/09/2026 (file 43) — cinq libelles en
//    dur, sans rapport avec le catalogue de modules du tenant, et dont les
//    cases cochees n etaient enregistrees NULLE PART. La formation d un agent
//    se gere par le module Formations (`formations_agents`, `modules_formation`,
//    `/formations/assign`), qui existe et fonctionne.

const EMPTY = {
  nom: '', prenom: '', date_naissance: '', telephone: '', type_contrat: 'CDI',
  date_embauche: '', poste: '', salaire_base: '',
  // ⚠️ CATEGORIE PROFESSIONNELLE — AUCUNE VALEUR PAR DEFAUT, JAMAIS.
  //    Classer un agent fixe son salaire plancher (CCIT art. 35) : c'est une
  //    decision RH opposable, pas une commodite de saisie. Un cran pre-selectionne
  //    serait une decision prise par le logiciel (regle 41).
  cran: '', cran_origine: '', cran_date: '',
  // ⚠️ Numero d affilie CNSS : attribue par la CAISSE. Le systeme ne le
  //    calcule pas, ne le derive pas du matricule, ne le pre-remplit pas.
  numero_cnss: '',
};


const KPI_CARDS = [
  { key: 'en_service', label: 'En service', color: '#2e7d32', icon: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' },
  { key: 'en_conge', label: 'En conge', color: '#1565c0', icon: 'M19 4H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM9 2v2M15 2v2M5 10h14' },
  { key: 'en_permission', label: 'En permission', color: '#f57c00', icon: 'M12 2a10 10 0 100 20 10 10 0 000-20zM12 6v6l4 2' },
  { key: 'absents', label: 'Absents', color: '#e53935', icon: 'M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0zM12 9v4M12 17h.01' },
  { key: 'inactifs', label: 'Inactifs', color: '#9e9e9e', icon: 'M21 8v13H3V8M1 3h22v5H1zM10 12h4' },
];

export default function AgentsRHPage() {
  /* ⓘ LA MEME PERMISSION QUE L ECRAN DES OPERATIONS, ET C EST LE POINT : la
   *   creation est attachee a la FONCTION, pas a l ecran (Dom, 16/09/2026).
   *   Conditionner un seul des deux boutons aurait fait de la disparition une
   *   propriete de l ecran — et le prochain ecran serait reparti sans garde. */
  const { user } = useAuth();
  const roles = (user && user.roles) || [];
  /* ⓘ LA DEVISE DU TENANT, LUE — jamais écrite en dur. `public.tenants.devise`
   *   via `/api/v1/tenant/branding`. Non résolue → on n'affiche RIEN : un
   *   montant dans la mauvaise devise est pire que sans unité. */
  const branding = useTenant();
  const devise = (branding && branding.devise) || '';

  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Les crans du DROIT DU TENANT. Liste vide = pays sans bareme : l'ecran le
  // DIT et ne propose rien, il ne retombe pas sur le bareme d'un autre pays.
  const [crans, setCrans] = useState([]);
  const [cransMessage, setCransMessage] = useState('');
  const [classifActuelle, setClassifActuelle] = useState(null);
  // Le referentiel des postes DU TENANT. Vide = aucun poste : l'ecran le dit
  // au lieu de proposer une liste qui ne lui appartient pas.
  const [postesRef, setPostesRef] = useState([]);
  const [error, setError] = useState('');
  // Constat rendu par le serveur apres une correction de date d embauche.
  const [constat, setConstat] = useState('');
  const [filterPoste, setFilterPoste] = useState('');
  const [filterContrat, setFilterContrat] = useState('');
  const [filterStatut, setFilterStatut] = useState('');

  /* ═══════════════════════════════════════════════════════════════════════
   * LE FILTRE DES POPULATIONS — lot « charte d affichage », 18/10/2026
   * ═══════════════════════════════════════════════════════════════════════
   * 🔴 CE QU IL REMPLACE. Le sous-menu RH « Personnel administratif » ouvrait
   *    un ecran qui, malgre son nom, lisait `gladiator_sec.agents` — il
   *    affichait donc LA MEME LISTE que celui-ci, en laissant croire a une
   *    seconde population geree a part. Et la table `personnel_admin` qu il
   *    pretendait servir porte **zero ligne en production** (mesure du 18/10).
   * ➡️ LE BESOIN REEL ETAIT DE DISTINGUER LES POPULATIONS, et une population
   *    se DECLARE. Le filtre lit donc `populationsDeclarees`, le referentiel du
   *    tenant — **rien n est ecrit en dur ici** : aucun libelle, aucun code,
   *    aucune liste. Un tenant qui n a declare aucune population voit un filtre
   *    qui le DIT, pas un filtre vide.
   * ⓘ `''` = « Toutes populations ». `SANS` = les salaries qu aucune population
   *   ne porte : ils existent (2 chez FOX, 15 chez VIGIE au 18/10), et sans
   *   cette entree le filtre les rendrait INTROUVABLES — *un filtre qui cache
   *   des gens est pire que pas de filtre*. L entree n apparait QUE s il y en a.
   * ═══════════════════════════════════════════════════════════════════════ */
  const [filterPopulation, setFilterPopulation] = useState('');
  const [situation, setSituation] = useState(null);
  const [activeKpi, setActiveKpi] = useState(null);

  /* ── LES PERSONNES « À VALIDER » — Dom, 12/10/2026 ──────────────────────────
   * ⓘ Elles viennent d'un compte créé dans Administration → Utilisateurs, dont
   *   quelqu'un a répondu « oui, elle est salariée ». Elles **n'existent pas
   *   dans `gladiator_sec.agents`** : ni dans les KPI de cet écran, ni dans
   *   l'effectif, ni dans la paie, ni dans la facture. C'est la validation qui
   *   crée le salarié — avec son matricule, sa date d'embauche, son salaire et
   *   sa population.
   * ⚠️ ELLES SONT DONC AILLEURS QUE DANS `items`, et ce n'est pas une bizarrerie
   *   d'implémentation : c'est la garantie. *Une pré-fiche rangée avec les
   *   salariés finirait par être comptée comme telle par une requête écrite
   *   demain.* */
  const [aValider, setAValider] = useState([]);
  const [populationsDeclarees, setPopulationsDeclarees] = useState([]);
  const [validation, setValidation] = useState(null);
  const [validForm, setValidForm] = useState({
    date_embauche: '', salaire_base: '', population: '', matricule: '',
    poste: '', type_contrat: '', nom: '', prenom: '',
  });
  const [validErreur, setValidErreur] = useState('');

  /* ── « FAIRE ÉVOLUER » — maquette ② validée par Dom le 13/10/2026 ──────────
   * 🎯 « UN seul geste pour poste ET population. Le formulaire reprend les
   *    valeurs en vigueur ; la RH ne modifie que ce qui change, avec date
   *    d'effet et motif. Le poste SORT du formulaire de modification
   *    ordinaire. »
   * ⓘ `evoluer` porte le salarié concerné ET sa carrière lue au serveur : le
   *   pré-remplissage vient de la SITUATION en vigueur, pas de la ligne du
   *   tableau. *La fiche n'est qu'un reflet, et elle ne porte pas la date
   *   d'effet — s'en servir pour pré-remplir ferait perdre l'information qui
   *   compte.* */
  const [evoluer, setEvoluer] = useState(null);
  const [evoluerForm, setEvoluerForm] = useState({
    poste: '', population_id: '', date_effet: '', motif: '',
  });
  const [evoluerErreur, setEvoluerErreur] = useState('');
  const [evoluerAlertes, setEvoluerAlertes] = useState([]);
  const [populationsRef, setPopulationsRef] = useState([]);

  useEscapeClose(showForm, () => setShowForm(false));
  useEscapeClose(!!evoluer, () => setEvoluer(null));


  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [agentsRes, sitRes, avRes] = await Promise.all([
        api.get('/agents'),
        api.get('/agents/situation'),
        /* ⓘ Un échec ici ne vide pas l'écran : les personnes à valider sont une
         *   information de plus, pas la liste du personnel. */
        api.get('/qualification-comptes/a-valider')
          .catch(() => ({ data: { a_valider: [], populations_declarees: [] } })),
      ]);
      setItems(agentsRes.data);
      setSituation(sitRes.data);
      setAValider((avRes.data && avRes.data.a_valider) || []);
      setPopulationsDeclarees((avRes.data && avRes.data.populations_declarees) || []);
    } catch {} finally { setLoading(false); }
  }, []);

  /* ── VALIDER : C'EST ICI QUE LE SALARIÉ COMMENCE À EXISTER ───────────────── */
  const validerFiche = async () => {
    setValidErreur('');
    /* ⚠️ LE MONTANT SE NORMALISE AVANT D'ÊTRE JUGÉ — défaut mesuré à l'écran le
     *    12/10/2026. La RH a tapé « 100 000 » et l'écran a répondu « le salaire
     *    est obligatoire » : le champ était rempli à ses yeux et vide dans le
     *    code (l'`<input type="number">` rend `""` sur une saisie qu'il ne
     *    reconnaît pas). ➡️ Le champ est maintenant du TEXTE, et les espaces —
     *    y compris les insécables d'un copier-coller — sont retirées ici.
     * ⓘ Le serveur fait la même lecture (`utils/montant.js`) : c'est lui qui
     *   fait foi. Ce contrôle-ci ne sert qu'à éviter un aller-retour. */
    const salaireNormalise = String(validForm.salaire_base || '').replace(ESPACES_MONTANT, '');
    const absents = [];
    if (!validForm.date_embauche) absents.push('la date d\'embauche');
    if (salaireNormalise === '') absents.push('le salaire de base');
    if (!validForm.population) absents.push('la population');
    if (absents.length > 0) {
      setValidErreur(`${absents.join(', ')} ${absents.length > 1 ? 'sont obligatoires' : 'est obligatoire'}.`);
      return;
    }
    /* ⚠️ « REMPLI MAIS PAS UN MONTANT » N'EST PAS « ABSENT », et les deux refus
     *    ne se confondent plus : dire « obligatoire » à quelqu'un qui vient de
     *    saisir une valeur l'accuse de ne pas avoir fait ce qu'il a fait. */
    if (!/^\d+(\.\d{1,2})?$/.test(salaireNormalise)) {
      setValidErreur(salaireNormalise.includes(',')
        ? `Le salaire de base contient une virgule (« ${validForm.salaire_base} »). `
          + 'Utiliser un point pour les décimales. Les espaces, elles, sont acceptées.'
        : `Le salaire de base n'est pas un montant (« ${validForm.salaire_base} »). `
          + 'Attendu : des chiffres, avec ou sans espaces — « 100000 » ou « 100 000 ».');
      return;
    }
    setSaving(true);
    try {
      /* ⚠️ ON N'ENVOIE QUE CE QUI EST RENSEIGNÉ : la route REFUSE un champ
       *    qu'elle n'accepte pas, et une chaîne vide n'est pas une valeur. */
      const corps = {
        date_embauche: validForm.date_embauche,
        salaire_base: salaireNormalise,
        population: validForm.population,
      };
      for (const k of ['matricule', 'poste', 'type_contrat', 'nom', 'prenom']) {
        if (validForm[k] && String(validForm[k]).trim()) corps[k] = String(validForm[k]).trim();
      }
      const { data } = await api.post(
        `/qualification-comptes/${validation.id}/valider`, corps);
      setValidation(null);
      setConstat(data.message || '');
      load();
    } catch (err) {
      setValidErreur(err.response?.data?.error || 'Validation impossible');
    }
    setSaving(false);
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    /* ⓘ LES POPULATIONS DU TENANT, pour « Faire évoluer ». Liste vide = aucune
     *   déclarée : la modale le DIT au lieu de proposer un menu creux. */
    api.get('/populations')
      .then(({ data }) => setPopulationsRef(
        (Array.isArray(data) ? data : (data.populations || [])).filter((p) => p.actif)))
      .catch(() => { /* le champ dira qu'il n'y en a aucune */ });
    api.get('/postes')
      .then(({ data }) => setPostesRef(data.postes || []))
      .catch(() => { /* le champ reste vide et le dira, la page ne casse pas */ });
  }, []);

  useEffect(() => {
    api.get('/classifications/crans')
      .then(({ data }) => { setCrans(data.crans || []); setCransMessage(data.message || ''); })
      .catch(() => { /* champ inutilisable, pas de page cassee */ });
  }, []);

  const openCreate = () => {
    setEditId(null); setForm(EMPTY); setClassifActuelle(null); setError(''); setShowForm(true);
  };
  const openEdit = async (a) => {
    setEditId(a.id);
    setForm({
      nom: a.nom || '', prenom: a.prenom || '', date_naissance: a.date_naissance?.split('T')[0] || '',
      telephone: a.telephone || '', type_contrat: a.type_contrat || 'CDI',
      date_embauche: a.date_embauche?.split('T')[0] || '', poste: a.poste || '',
      salaire_base: a.salaire_base || '', 
      numero_cnss: a.numero_cnss || '',
      cran: '', cran_origine: '',
      // La date d'effet PROPOSEE est la date d'embauche — jamais celle du jour.
      // Classer aujourd'hui un agent embauche en 2023 ferait demarrer sa
      // classification en 2026 et rendrait tout son historique de paie
      // injustifiable devant un controle CNSS.
      cran_date: a.date_embauche?.split('T')[0] || '',
    });
    setClassifActuelle(null);
    setError(''); setShowForm(true);
    // Le cran courant est lu sur la FICHE, par le point unique cote serveur.
    try {
      const { data } = await api.get(`/agents/${a.id}`);
      setClassifActuelle(data.classification || null);
    } catch { /* la modale reste utilisable sans : le champ dira « inconnue » */ }
  };

  /* ── OUVRIR « FAIRE ÉVOLUER » ─────────────────────────────────────────────
   * ⚠️ LES VALEURS EN VIGUEUR SONT LUES AU SERVEUR, PAS COPIÉES DE LA LIGNE.
   *    La ligne du tableau porte `agents.poste`, qui est une PROJECTION — elle
   *    dit le poste d'aujourd'hui, pas depuis quand il l'est. Et elle ne porte
   *    pas la population. *Pré-remplir depuis le reflet ferait perdre
   *    exactement ce que ce lot ajoute.*
   * ⛔ LA DATE D'EFFET N'EST PAS PRÉ-REMPLIE, et surtout pas à aujourd'hui :
   *    une promotion décidée en réunion la semaine dernière prendrait effet au
   *    mauvais jour, et le bulletin suivrait. */
  const ouvrirEvoluer = async (a) => {
    setEvoluerErreur(''); setEvoluerAlertes([]);
    setEvoluer({ id: a.id, nom: a.nom, prenom: a.prenom, carriere: null });
    setEvoluerForm({ poste: '', population_id: '', date_effet: '', motif: '' });
    try {
      const { data } = await api.get(`/agents/${a.id}/carriere`);
      setEvoluer((e) => (e && e.id === a.id ? { ...e, carriere: data } : e));
      setEvoluerForm({
        poste: (data.en_vigueur && data.en_vigueur.poste) || '',
        population_id: (data.en_vigueur && data.en_vigueur.population_id) || '',
        date_effet: '', motif: '',
      });
    } catch (err) {
      setEvoluerErreur(err?.response?.data?.error
        || 'La carrière de ce salarié n’a pas pu être lue. Aucun changement n’est possible '
           + 'sans savoir ce qui est en vigueur.');
    }
  };

  const soumettreEvoluer = async (e) => {
    e.preventDefault(); setEvoluerErreur(''); setEvoluerAlertes([]); setSaving(true);
    try {
      /* ⓘ ON N'ENVOIE QUE CE QUI A UNE VALEUR, et le serveur garde le reste en
       *   vigueur. Envoyer `''` voudrait dire « retire-le » — ce n'est pas la
       *   même décision, et les deux ne doivent pas se confondre. */
      const charge = { date_effet: evoluerForm.date_effet, motif: evoluerForm.motif.trim() };
      if (evoluerForm.poste) charge.poste = evoluerForm.poste;
      if (evoluerForm.population_id) charge.population_id = evoluerForm.population_id;
      const { data } = await api.post(`/agents/${evoluer.id}/faire-evoluer`, charge);
      await load();
      /* ⚠️ LA MODALE RESTE OUVERTE QUAND IL Y A UNE ALERTE. La fermer ferait
       *    disparaître le constat au moment même où il arrive — et la RH
       *    découvrirait le mois suivant, sur un bulletin, qu'un changement au 15
       *    n'a pas été proraté. */
      if (data.alertes && data.alertes.length > 0) {
        setEvoluerAlertes(data.alertes);
        const { data: c } = await api.get(`/agents/${evoluer.id}/carriere`);
        setEvoluer((v) => (v ? { ...v, carriere: c } : v));
        setEvoluerForm((f) => ({ ...f, date_effet: '', motif: '' }));
      } else {
        setEvoluer(null);
      }
    } catch (err) {
      setEvoluerErreur(err?.response?.data?.error || 'L’évolution a été refusée.');
    } finally { setSaving(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      /* 🔴 CE QUI NE VA PAS A `/agents` EN EST RETIRE, NOMMEMENT — 16/09/2026.
       * Depuis ce lot, le serveur REFUSE une cle qu il ne connait pas au lieu
       * de l ignorer (`CHAMPS_INCONNUS`). Il faut donc ne lui envoyer que ce
       * qui le concerne.
       * ⚠️ EXCLUSION NOMMEE, ET SURTOUT PAS LISTE BLANCHE : une liste blanche
       *    filtrerait aussi un champ NEUF oublie au catalogue, et retablirait
       *    le silence qu on vient de fermer, un cran plus haut. Ici on ne
       *    connait QUE ce qui va ailleurs ; tout le reste part et se fait
       *    refuser s il est inconnu.
       * ⓘ `cran`/`cran_origine`/`cran_date` -> `/classifications/appliquer`,
       *   plus bas : la categorie est une ligne DATEE a part.
       * ⓘ `formations` et `notes` ne figurent PLUS dans cette exclusion : ils
       *   ont ete retires du formulaire le 16/09 (file 43). Ils n etaient des
       *   colonnes NULLE PART et se perdaient en silence. */
      /* 🔴 `poste` EST RETIRÉ DE LA MODIFICATION — C2b, 13/10/2026.
       * ⚠️ IL RESTE ENVOYÉ À LA CRÉATION : c'est la première valeur de la
       *    carrière, et le serveur ouvre la situation avec elle. Mais sur un
       *    salarié EXISTANT, `PUT /agents/:id` le REFUSE en nommant
       *    « Faire évoluer » (`CHAMPS_PAR_SITUATION`) — l'envoyer ferait donc
       *    échouer l'enregistrement de tout le reste de la fiche.
       * ⓘ Même raison que l'exclusion du cran juste au-dessus : ce qui est une
       *   ligne DATÉE ne voyage pas dans la fiche. */
      const { cran, cran_origine, cran_date, ...fiche } = form;
      let agentId = editId;
      if (editId) {
        delete fiche.poste;
        const { data } = await api.put(`/agents/${editId}`, fiche);
        if (data && data.constats && data.constats.length > 0) {
          setConstat(data.constats.map(c => c.message).join(' '));
        }
      } else {
        const { data } = await api.post('/agents', fiche);
        agentId = data.id;
      }
      // ── LE CLASSEMENT EST UNE ECRITURE A PART ─────────────────────────────
      // Il ne passe PAS par `/agents` : la categorie vit dans
      // `agents_classifications`, une ligne DATEE avec son propre journal. La
      // faire voyager dans la fiche agent recreerait la colonne morte qu'on
      // vient de retirer.
      // ⚠️ On ne l'envoie que si un cran a ete CHOISI. Aucun envoi implicite.
      if (form.cran && form.cran_date && !classifActuelle) {
        const { data } = await api.post('/classifications/appliquer', {
          code: form.cran,
          origine: form.cran_origine || 'pays',
          agents: [{ agent_id: agentId, date_debut: form.cran_date }],
        });
        // Un ecart ne se tait pas, meme sur un seul agent : la fiche est
        // enregistree, le classement ne l'est pas — il faut le dire.
        if (data.ecartes && data.ecartes.length > 0) {
          setError(`Fiche enregistree, mais le classement n'a PAS ete pose : ${data.ecartes[0].raison}`);
          setSaving(false); load(); return;
        }
      }
      setShowForm(false); setForm(EMPTY); setEditId(null); setClassifActuelle(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Erreur'); } finally { setSaving(false); }
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));


  const filtered = items.filter(a => {
    if (filterPoste && a.poste !== filterPoste) return false;
    if (filterContrat && a.type_contrat !== filterContrat) return false;
    if (filterStatut && a.statut !== filterStatut) return false;
    /* ⓘ `SANS` : les salaries sans population declaree. On compare a `null` ET
     *   a la chaine vide — une colonne nullable remplie par un formulaire peut
     *   porter l une ou l autre. */
    if (filterPopulation === 'SANS' && (a.population_id !== null && a.population_id !== undefined && a.population_id !== '')) return false;
    if (filterPopulation && filterPopulation !== 'SANS' && a.population_id !== filterPopulation) return false;
    return true;
  });

  const total = items.length;
  const cdi = items.filter(a => a.type_contrat === 'CDI').length;
  const cdd = items.filter(a => a.type_contrat === 'CDD').length;
  const essai = items.filter(a => a.type_contrat === 'Periode_essai').length;

  const statCards = [
    { label: 'Total agents', value: total, color: '#1565c0' },
    { label: 'CDI', value: cdi, color: '#2e7d32' },
    { label: 'CDD', value: cdd, color: '#1565c0' },
    { label: "Période d'essai", value: essai, color: '#f57c00' },
  ];

  const handleKpiClick = (key) => {
    setActiveKpi(prev => prev === key ? null : key);
  };

  // Detail table for active KPI
  const renderKpiDetail = () => {
    if (!activeKpi || !situation?.details) return null;
    const detailMap = {
      en_conge: 'conges',
      en_permission: 'permissions',
      absents: 'absents',
      inactifs: 'inactifs',
    };
    if (activeKpi === 'en_service') {
      // En service = actifs with pointage, filter from main list
      const congeIds = new Set((situation.details.conges || []).map(c => c.agent_id));
      const permIds = new Set((situation.details.permissions || []).map(p => p.agent_id));
      const absentIds = new Set((situation.details.absents || []).map(a => a.agent_id));
      const enServiceAgents = items.filter(a => a.statut === 'actif' && !congeIds.has(a.id) && !permIds.has(a.id) && !absentIds.has(a.id));
      return (
        <div className="content-card" style={{ marginBottom: '1rem' }}>
          <div className="content-card-title" style={{ color: '#2e7d32' }}>Agents en service ({enServiceAgents.length})</div>
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead><tr><th>Matricule</th><th>Nom</th><th>Prénom</th><th>Poste</th><th>Statut</th></tr></thead>
              <tbody>
                {enServiceAgents.map(a => (
                  <tr key={a.id}>
                    <td><strong>{a.matricule}</strong></td>
                    <td>{a.nom}</td>
                    <td>{a.prenom}</td>
                    <td style={{ textTransform: 'capitalize' }}>{a.poste?.replace(/_/g, ' ') || '-'}</td>
                    <td><span className="gss-badge gss-badge-green">Actif</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {enServiceAgents.length === 0 && <p style={{ textAlign: 'center', padding: '1rem', color: '#999' }}>Aucun agent en service</p>}
        </div>
      );
    }

    const dataKey = detailMap[activeKpi];
    const data = situation.details[dataKey] || [];
    const card = KPI_CARDS.find(k => k.key === activeKpi);

    if (activeKpi === 'en_conge' || activeKpi === 'en_permission') {
      return (
        <div className="content-card" style={{ marginBottom: '1rem' }}>
          <div className="content-card-title" style={{ color: card.color }}>{card.label} ({data.length})</div>
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead><tr><th>Matricule</th><th>Agent</th><th>Type</th><th>Debut</th><th>Fin</th><th>Retour prevu</th></tr></thead>
              <tbody>
                {data.map((d, i) => {
                  const retour = new Date(d.date_fin);
                  retour.setDate(retour.getDate() + 1);
                  return (
                    <tr key={i}>
                      <td><strong>{d.matricule}</strong></td>
                      <td>{d.prenom} {d.nom}</td>
                      <td><span className="gss-badge" style={{ background: card.color + '20', color: card.color }}>{d.type}</span></td>
                      <td>{formatDate(d.date_debut)}</td>
                      <td>{formatDate(d.date_fin)}</td>
                      <td style={{ fontWeight: 600 }}>{formatDate(retour.toISOString())}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data.length === 0 && <p style={{ textAlign: 'center', padding: '1rem', color: '#999' }}>Aucun agent</p>}
        </div>
      );
    }

    if (activeKpi === 'absents') {
      return (
        <div className="content-card" style={{ marginBottom: '1rem' }}>
          <div className="content-card-title" style={{ color: card.color }}>Agents absents ({data.length})</div>
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead><tr><th>Matricule</th><th>Agent</th><th>Poste</th><th>Statut</th></tr></thead>
              <tbody>
                {data.map((d, i) => (
                  <tr key={i}>
                    <td><strong>{d.matricule}</strong></td>
                    <td>{d.prenom} {d.nom}</td>
                    <td style={{ textTransform: 'capitalize' }}>{d.poste?.replace(/_/g, ' ') || '-'}</td>
                    <td><span className="gss-badge" style={{ background: '#e5393520', color: '#e53935' }}>Absent</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.length === 0 && <p style={{ textAlign: 'center', padding: '1rem', color: '#999' }}>Aucun absent</p>}
        </div>
      );
    }

    if (activeKpi === 'inactifs') {
      return (
        <div className="content-card" style={{ marginBottom: '1rem' }}>
          <div className="content-card-title" style={{ color: card.color }}>Agents inactifs ({data.length})</div>
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead><tr><th>Matricule</th><th>Agent</th><th>Poste</th><th>Statut</th></tr></thead>
              <tbody>
                {data.map((d, i) => (
                  <tr key={i}>
                    <td><strong>{d.matricule}</strong></td>
                    <td>{d.prenom} {d.nom}</td>
                    <td style={{ textTransform: 'capitalize' }}>{d.poste?.replace(/_/g, ' ') || '-'}</td>
                    <td><span className="gss-badge" style={{ background: '#9e9e9e20', color: '#9e9e9e' }}>Inactif</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.length === 0 && <p style={{ textAlign: 'center', padding: '1rem', color: '#999' }}>Aucun agent inactif</p>}
        </div>
      );
    }
    return null;
  };

  return (
    <div>
      {/* ══ BANDEAU : DES PERSONNES ATTENDENT LEUR FICHE ══════════════════════
          ⓘ Il DISPARAÎT quand la liste est vide — un bandeau permanent qui
            annonce « rien à faire » est un bandeau qu'on cesse de lire. */}
      {aValider.length > 0 && (
        <div className="content-card" style={{ marginBottom: '1rem', border: '2px solid #B45309' }}>
          <div className="content-card-title" style={{ marginBottom: 6, color: '#B45309' }}>
            À valider ({aValider.length})
          </div>
          <div style={{ fontSize: 13, color: 'var(--gss-text-secondary)', marginBottom: 10 }}>
            Ces personnes ont un compte, et quelqu&rsquo;un a répondu
            qu&rsquo;elles sont salariées de la société. Elles ne sont
            <strong> pas encore des salariés</strong> : elles ne figurent ni dans
            les compteurs ci-dessous, ni dans la paie, ni dans votre facture.
            Complétez leur fiche pour qu&rsquo;elles entrent dans
            l&rsquo;effectif.
          </div>
          <div className="gss-table-wrap">
            <table className="gss-table">
              <thead>
                <tr><th>Personne</th><th>Compte</th><th>Motif</th><th style={{ width: 170 }}>Action</th></tr>
              </thead>
              <tbody>
                {aValider.map((p) => (
                  <tr key={p.id}>
                    <td><strong>{p.prenom} {p.nom}</strong></td>
                    <td style={{ color: 'var(--gss-text-secondary)', fontSize: '0.85rem' }}>
                      {p.email}
                      {!p.compte_actif && (
                        <span className="gss-badge" style={{ marginLeft: 6, background: 'rgba(220,38,38,0.08)', color: '#DC2626' }}>
                          compte inactif
                        </span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{p.motif}</td>
                    <td>
                      <button className="gss-btn-sm" onClick={() => {
                        setValidation(p);
                        setValidErreur('');
                        setValidForm({
                          date_embauche: '', salaire_base: '',
                          /* ⛔ AUCUNE POPULATION PRÉ-SÉLECTIONNÉE, MÊME S'IL N'Y
                             EN A QU'UNE. La population commande le mode de paie,
                             la convention et les rubriques : c'est une décision
                             RH, et le rôle du compte n'y entre pas. */
                          population: '', matricule: '', poste: '', type_contrat: '',
                          /* ⓘ L'identité, ELLE, se pré-remplit : elle vient du
                             compte et la RH la corrige si besoin. Un prénom
                             absent reste absent — le produit n'en invente pas. */
                          nom: p.nom || '', prenom: p.prenom || '',
                        });
                      }}>
                        Compléter la fiche
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══ MODALE : COMPLÉTER ET VALIDER UNE FICHE ═══════════════════════════ */}
      {validation && (
        <div className="gss-modal-overlay" onClick={() => setValidation(null)}>
          <div className="gss-modal gss-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>
                <div>
                  Fiche de {validation.prenom} {validation.nom}
                  <div className="gss-modal-subtitle">
                    La validation crée le salarié — il entre alors dans l&rsquo;effectif
                  </div>
                </div>
              </h2>
              <button className="gss-modal-close" onClick={() => setValidation(null)}>&times;</button>
            </div>
            {validErreur && (
              <div style={{ padding: '0.5rem 1.5rem', color: '#e53935', fontSize: '0.9rem' }}>
                {validErreur}
              </div>
            )}
            <div className="gss-form-grid" style={{ padding: '1.5rem' }}>
              {/* ⚠️ L'IDENTITÉ EST VISIBLE ET CORRIGEABLE, et ce n'était pas le
                  cas : `gladiator_sec.agents.prenom` est NOT NULL alors que le
                  compte peut ne pas en porter — un compte sans prénom rendait
                  une « Erreur interne du serveur » (mesuré le 12/10).
                  ⓘ La fiche de salarié EST l'identité qui fait foi ; le compte
                    n'est que l'endroit d'où elle est née. La RH corrige donc
                    ici, et le produit n'invente jamais un prénom. */}
              {/* ⚠️ `autoComplete="off"` SUR L'IDENTITÉ, ET CE N'EST PAS UNE
                  PRÉCAUTION DE STYLE. Le 12/10, une fiche validée a porté le
                  prénom « administratif » que ni le compte ni la pré-fiche ne
                  contenaient (les deux à NULL, vérifié en base et dans la
                  trace d'audit). Le produit ne pouvait pas l'inventer : aucun
                  découpage de nom complet n'existe dans le code, et le prénom
                  n'est lu ni d'un rôle ni d'une population. Restait la main ou
                  le navigateur — et la porte du navigateur, elle, se ferme.
                  ⓘ On ferme donc ce qu'on peut fermer, plutôt que de conclure
                    sur ce qu'on ne peut pas mesurer. */}
              <div className="gss-field">
                <label className="gss-label">Nom *</label>
                <input className="gss-input" name="validation-nom" autoComplete="off"
                  value={validForm.nom}
                  onChange={(e) => setValidForm(f => ({ ...f, nom: e.target.value }))} />
              </div>
              <div className="gss-field">
                <label className="gss-label">Prénom *</label>
                <input className="gss-input" name="validation-prenom" autoComplete="off"
                  value={validForm.prenom} placeholder="À renseigner"
                  onChange={(e) => setValidForm(f => ({ ...f, prenom: e.target.value }))} />
                {!validForm.prenom && (
                  <small style={{ color: '#B45309' }}>
                    Ce compte ne porte pas de prénom, et une fiche de salarié en
                    exige un : saisissez-le. Rien n&rsquo;est deviné à partir du
                    nom, du rôle ni de la population.
                  </small>
                )}
              </div>
              <div className="gss-field">
                <label className="gss-label">Date d&rsquo;embauche *</label>
                <input className="gss-input" type="date" value={validForm.date_embauche}
                  onChange={(e) => setValidForm(f => ({ ...f, date_embauche: e.target.value }))} />
                {/* ⓘ Une date d'histoire ne se devine pas : aucune valeur du jour
                    n'est proposée (file 37). */}
                <small style={{ color: 'var(--gss-text-secondary)' }}>
                  La date réelle d&rsquo;entrée dans la société, pas celle du jour.
                </small>
              </div>
              <div className="gss-field">
                {/* ⚠️ LA DEVISE VIENT DU TENANT, JAMAIS DU CODE. Elle est lue sur
                    `public.tenants.devise` via `/api/v1/tenant/branding`
                    (`useTenant()`), comme le fait déjà l'écran des contrats. Un
                    tenant camerounais est en XAF, pas en XOF — et « FCFA » écrit
                    en dur serait faux pour la moitié de la zone.
                    ⛔ NON RÉSOLUE = RIEN D'AFFICHÉ. *Un montant affiché dans la
                       mauvaise devise est pire que sans unité.* */}
                <label className="gss-label">
                  Salaire de base{devise ? ` (${devise})` : ''} *
                </label>
                {/* ⛔ PAS `type="number"`, ET C'EST LE CORRECTIF : un champ
                    numérique rend une valeur VIDE dès que la saisie n'est pas un
                    nombre à ses yeux — « 100 000 » en est une. La RH voyait son
                    montant à l'écran et le produit répondait « obligatoire ».
                    ⓘ `inputMode="decimal"` garde le pavé numérique sur mobile
                      sans imposer la grammaire du navigateur. */}
                <input className="gss-input" type="text" inputMode="decimal"
                  name="validation-salaire" autoComplete="off"
                  value={validForm.salaire_base} placeholder="ex. : 100 000"
                  onChange={(e) => setValidForm(f => ({ ...f, salaire_base: e.target.value }))}
                  /* ⓘ REMISE EN FORME À LA SORTIE DU CHAMP, PAS PENDANT LA FRAPPE.
                   *   Reformater à chaque touche déplace le curseur et rend la
                   *   saisie désagréable ; le faire à la sortie montre le montant
                   *   tel qu'on l'écrit, une fois qu'il est complet.
                   * ⚠️ L'AFFICHAGE CHANGE, LA VALEUR ENVOYÉE NON : `validerFiche`
                   *    retire les espaces avant l'envoi, et c'est le serveur qui
                   *    fait foi (`utils/montant.js`). */
                  onBlur={() => setValidForm((f) => {
                    const brut = String(f.salaire_base || '')
                      .replace(ESPACES_MONTANT, '');
                    if (!/^\d+(\.\d{1,2})?$/.test(brut)) return f;
                    const [entier, dec] = brut.split('.');
                    const groupe = entier.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                    return { ...f, salaire_base: dec ? `${groupe}.${dec}` : groupe };
                  })} />
                <small style={{ color: 'var(--gss-text-secondary)' }}>
                  Les espaces sont acceptées : « 100 000 » comme « 100000 ».
                </small>
              </div>
              <div className="gss-field">
                <label className="gss-label">Population *</label>
                <select className="gss-input" value={validForm.population}
                  onChange={(e) => setValidForm(f => ({ ...f, population: e.target.value }))}>
                  <option value="">-- Choisir --</option>
                  {populationsDeclarees.map((p) => (
                    <option key={p.code} value={p.code}>{p.libelle}</option>
                  ))}
                </select>
                {populationsDeclarees.length === 0 ? (
                  <small style={{ color: '#B45309' }}>
                    Aucune population n&rsquo;est déclarée. Déclarez-en une dans le
                    paramétrage de la paie : sans elle, aucun bulletin ne peut être émis.
                  </small>
                ) : (
                  <small style={{ color: 'var(--gss-text-secondary)' }}>
                    Elle commande le mode de paie, la convention et les rubriques.
                    C&rsquo;est vous qui la choisissez — jamais le rôle du compte.
                  </small>
                )}
              </div>
              <div className="gss-field">
                <label className="gss-label">Matricule</label>
                <input className="gss-input" value={validForm.matricule}
                  placeholder="Laisser vide pour qu'il soit généré"
                  onChange={(e) => setValidForm(f => ({ ...f, matricule: e.target.value }))} />
                <small style={{ color: 'var(--gss-text-secondary)' }}>
                  Vide : le produit le génère. Saisi : c&rsquo;est le vôtre, et il
                  doit être unique dans la société.
                </small>
              </div>
              <div className="gss-field">
                <label className="gss-label">Poste</label>
                <select className="gss-input" value={validForm.poste}
                  onChange={(e) => setValidForm(f => ({ ...f, poste: e.target.value }))}>
                  <option value="">-- Aucun --</option>
                  {postesRef.map((p) => (
                    <option key={p.id} value={p.code}>{p.titre}</option>
                  ))}
                </select>
                {postesRef.length === 0 && <small style={{ color: '#B45309' }}>{AUCUN_POSTE}</small>}
              </div>
              <div className="gss-field">
                <label className="gss-label">Type de contrat</label>
                <select className="gss-input" value={validForm.type_contrat}
                  onChange={(e) => setValidForm(f => ({ ...f, type_contrat: e.target.value }))}>
                  <option value="">-- Non renseigné --</option>
                  {Object.keys(CONTRAT_LABELS).map((k) => (
                    <option key={k} value={k}>{CONTRAT_LABELS[k]}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="gss-modal-actions">
              <button type="button" className="gss-btn-cancel" onClick={() => setValidation(null)}>
                Annuler
              </button>
              <button type="button" className="gss-btn-deploy" onClick={validerFiche} disabled={saving}>
                {saving ? 'Validation...' : 'Valider la fiche'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KPI Situation du personnel */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
        {KPI_CARDS.map(kpi => (
          <div
            key={kpi.key}
            onClick={() => handleKpiClick(kpi.key)}
            className="content-card"
            style={{
              padding: '1rem', textAlign: 'center', cursor: 'pointer',
              border: activeKpi === kpi.key ? `2px solid ${kpi.color}` : '1px solid var(--gss-border)',
              transition: 'all 0.2s',
            }}
          >
            <div style={{ marginBottom: 6 }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={kpi.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d={kpi.icon} />
              </svg>
            </div>
            <div style={{ fontSize: '1.8rem', fontWeight: 700, color: kpi.color }}>{situation?.[kpi.key] ?? '-'}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--gss-text-light)', marginTop: 2 }}>{kpi.label}</div>
          </div>
        ))}
      </div>

      {/* Bouton Tous pour reset */}
      {activeKpi && (
        <div style={{ marginBottom: '0.75rem' }}>
          <button className="gss-btn" onClick={() => setActiveKpi(null)} style={{ fontSize: 12 }}>
            Tous les agents
          </button>
        </div>
      )}

      {/* Detail table for clicked KPI */}
      {renderKpiDetail()}

      {/* Standard stat cards */}
      {!activeKpi && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {statCards.map(sc => (
              <div key={sc.label} className="content-card" style={{ padding: '1.2rem', textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: sc.color }}>{sc.value}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--gss-text-light)', marginTop: '0.25rem' }}>{sc.label}</div>
              </div>
            ))}
          </div>

          {/* 🔴 LE CONSTAT DES PIECES DEJA EMISES — Dom, 16/09/2026.
            * « Sans cette phrase, ma RH corrigera une date et attendra que les
            *   bulletins suivent. »
            * ⚠️ Il s affiche APRES l enregistrement, hors de la modale : la
            *    correction a eu lieu, on ne la bloque pas et on ne la fait pas
            *    confirmer. Le produit CONSTATE (regle 62) — un fait, un
            *    nombre, aucune consigne.
            * ⓘ Il se ferme a la main : une correction de date est rare, et le
            *   message doit survivre au rechargement de la liste. */}
          {constat && (
            <div className="gss-alert" style={{ background: '#eef4fb', border: '1px solid #c5d9ee', color: '#2c4a6b', marginBottom: '1rem' }}>
              {constat}
              <button onClick={() => setConstat('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', color: '#2c4a6b' }}>&times;</button>
            </div>
          )}

          <div className="content-card">
            <div className="gss-toolbar">
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <select className="gss-input" style={{ width: 'auto', minWidth: '140px' }} value={filterPoste} onChange={e => setFilterPoste(e.target.value)}>
                  <option value="">Tous les postes</option>
                  {/* Le FILTRE lit le même référentiel que le formulaire : deux
                      listes distinctes finiraient par ne plus filtrer ce qu'on
                      peut saisir. */}
                  {postesRef.map(p => <option key={p.code} value={p.code}>{p.titre}</option>)}
                </select>
                {/* ═══════════════════════════════════════════════════════════
                  * LE FILTRE DES POPULATIONS — il remplace un sous-menu entier
                  * ═══════════════════════════════════════════════════════════
                  * ⓘ IL LIT `populationsRef`, LE MEME REFERENTIEL QUE LA MODALE
                  *   « Faire evoluer » (`/populations`, filtre sur `actif`) :
                  *   deux listes distinctes finiraient par ne plus filtrer ce
                  *   qu on peut saisir — c est le raisonnement deja tenu pour
                  *   le filtre des postes, juste au-dessus.
                  * ⛔ RIEN N EST ECRIT EN DUR : ni libelle, ni code, ni ordre.
                  * ⓘ QUAND AUCUNE POPULATION N EST DECLAREE, le filtre le DIT
                  *   au lieu d offrir un menu creux — meme regle que la modale.
                  */}
                <select className="gss-input" style={{ width: 'auto', minWidth: '170px' }}
                  value={filterPopulation} onChange={e => setFilterPopulation(e.target.value)}
                  disabled={populationsRef.length === 0}
                  title={populationsRef.length === 0
                    ? 'Aucune population n’est déclarée pour ce client.'
                    : 'Filtrer sur une population déclarée'}>
                  <option value="">Toutes populations</option>
                  {populationsRef.map((p) => (
                    <option key={p.id} value={p.id}>{p.libelle}</option>
                  ))}
                  {/* L entree n apparait QUE s il existe des salaries sans
                      population : sinon elle proposerait un filtre vide. */}
                  {items.some((a) => !a.population_id) && (
                    <option value="SANS">Sans population déclarée</option>
                  )}
                </select>
                {populationsRef.length === 0 && (
                  <span style={{ color: 'var(--gss-text-light)', fontSize: '0.78rem' }}>
                    Aucune population n&rsquo;est d&eacute;clar&eacute;e pour ce client.
                  </span>
                )}
                <select className="gss-input" style={{ width: 'auto', minWidth: '130px' }} value={filterContrat} onChange={e => setFilterContrat(e.target.value)}>
                  <option value="">Tous contrats</option>
                  {Object.entries(CONTRAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select className="gss-input" style={{ width: 'auto', minWidth: '130px' }} value={filterStatut} onChange={e => setFilterStatut(e.target.value)}>
                  <option value="">Tous statuts</option>
                  {Object.entries(STATUT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <span style={{ color: 'var(--gss-text-light)', fontSize: '0.85rem' }}>{filtered.length} agent(s)</span>
              </div>
              {canAction(roles, 'agents', 'create') && (
                <button className="gss-btn gss-btn-primary" onClick={openCreate}>+ Nouvel agent</button>
              )}
            </div>

            {loading ? (
              <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p>
            ) : filtered.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
                  <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                </svg>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucun enregistrement</div>
                {/* ⓘ L INVITATION SUIT LE BOUTON. Sans cette condition, une
                  * fonction qui ne peut pas creer lirait « Cliquez sur
                  * + Nouvel agent » en face d un bouton qui n existe pas —
                  * c est-a-dire qu on lui expliquerait sa restriction par
                  * l absurde, ce que la regle 26 interdit. */}
                {canAction(roles, 'agents', 'create') && (
                  <div style={{ fontSize: 14, color: '#999' }}>Cliquez sur "+ Nouvel agent" pour commencer</div>
                )}
              </div>
            ) : (
              <div className="gss-table-wrap">
                <table className="gss-table">
                  <thead><tr><th style={{ width: 40 }}></th><th>Matricule</th><th>Nom</th><th>Prénom</th><th>Poste</th><th>Type contrat</th><th>Date embauche</th><th>Statut</th><th className="gss-col-actions" style={{ width: 90 }}>Actions</th></tr></thead>
                  <tbody>
                    {filtered.map(a => (
                      /* ═══════════════════════════════════════════════════════
                       * LA LIGNE ENTIERE OUVRE LA FICHE — 18/10/2026
                       * ═══════════════════════════════════════════════════════
                       * ⓘ Demande de Dom : « clic sur le nom ou la ligne ouvre
                       *   la fiche ». La cible n est donc pas le seul petit
                       *   crayon de la colonne Actions : *un nom qui ressemble a
                       *   un lien et qui ne s ouvre pas se clique deux fois.*
                       * ⚠️ `role` ET `tabIndex` SONT POSES, pas par formalisme :
                       *    une ligne cliquable qu on ne peut pas atteindre au
                       *    clavier retire une fonction a qui n a pas de souris.
                       *    `Entree` et `Espace` ouvrent la fiche.
                       * ⛔ ET LES BOUTONS D ACTION ARRETENT LA PROPAGATION (voir
                       *    la colonne Actions) : sans cela, « Faire evoluer »
                       *    ouvrirait sa modale PUIS la fiche par-dessus. */
                      <tr key={a.id} className="gss-row-cliquable"
                        role="button" tabIndex={0}
                        title="Ouvrir la fiche"
                        onClick={() => openEdit(a)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEdit(a); }
                        }}>
                        <td>
                          <div style={{ width: 32, height: 32, borderRadius: 6, background: '#8B1A1A', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>
                            {(a.prenom?.[0] || '')}{(a.nom?.[0] || '')}
                          </div>
                        </td>
                        <td><strong>{a.matricule || '-'}</strong></td>
                        {/* ⓘ LE NOM PORTE LA MARQUE DU LIEN — c est ce que l oeil
                          *   cherche. La ligne entiere est cliquable, mais rien
                          *   ne le disait : *une zone cliquable invisible n est
                          *   pas une fonction, c est une chance.* */}
                        <td><span className="gss-lien-fiche">{a.nom}</span></td>
                        <td><span className="gss-lien-fiche">{a.prenom}</span></td>
                        <td style={{ textTransform: 'capitalize' }}>{a.poste?.replace(/_/g, ' ') || '-'}</td>
                        <td><span className="gss-badge" style={{ background: (CONTRAT_COLORS[a.type_contrat] || '#999') + '20', color: CONTRAT_COLORS[a.type_contrat] || '#999' }}>{CONTRAT_LABELS[a.type_contrat] || a.type_contrat || '-'}</span></td>
                        <td>{formatDate(a.date_embauche)}</td>
                        <td><span className="gss-badge" style={{ background: (STATUT_COLORS[a.statut] || '#999') + '20', color: STATUT_COLORS[a.statut] || '#999' }}>{STATUT_LABELS[a.statut] || a.statut}</span></td>
                        {/* ⚠️ `stopPropagation` SUR LA CELLULE ENTIERE, pas
                          *    bouton par bouton : un bouton ajoute demain
                          *    heriterait du garde au lieu de l oublier. */}
                        <td className="gss-col-actions" onClick={(e) => e.stopPropagation()}>
                          <div className="gss-actions">
                            <button className="gss-btn-icon" onClick={() => openEdit(a)} title="Modifier"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></button>
                            {/* ② LE GESTE EST ATTEIGNABLE DEPUIS LA LISTE, pas
                                seulement depuis la fiche : une promotion se
                                décide en regardant l'effectif. */}
                            <button className="gss-btn-icon" onClick={() => ouvrirEvoluer(a)} title="Faire évoluer (poste, population)"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                <h2>{editId ? 'Modifier la fiche agent' : 'Nouvel agent'}</h2>
              </div>
              <div className="gss-modal-subtitle">Dossier RH de l'agent</div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            {error && <div style={{ padding: '0.5rem 1.5rem', color: '#e53935', fontSize: '0.9rem' }}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="gss-form-grid" style={{ padding: '1.5rem' }}>
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span>IDENTITÉ</span>
                </div>
                <div className="gss-field">
                  <label>Nom *</label>
                  <input className="gss-input" required value={form.nom} onChange={e => setField('nom', e.target.value)} />
                </div>
                <div className="gss-field">
                  <label>Prénom *</label>
                  <input className="gss-input" required value={form.prenom} onChange={e => setField('prenom', e.target.value)} />
                </div>
                <div className="gss-field">
                  <label>Date de naissance</label>
                  <input className="gss-input" type="date" value={form.date_naissance} onChange={e => setField('date_naissance', e.target.value)} />
                </div>
                <div className="gss-field">
                  <label>Téléphone</label>
                  <input className="gss-input" value={form.telephone} onChange={e => setField('telephone', e.target.value)} placeholder="+228 90 00 00 00" />
                </div>

                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  <span>CONTRAT & POSTE</span>
                </div>
                <div className="gss-field">
                  <label>Type de contrat *</label>
                  <select className="gss-input" required value={form.type_contrat} onChange={e => setField('type_contrat', e.target.value)}>
                    {Object.entries(CONTRAT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="gss-field">
                  <label>Date d'embauche *</label>
                  <input className="gss-input" type="date" required value={form.date_embauche} onChange={e => setField('date_embauche', e.target.value)} />
                </div>
                <div className="gss-field">
                  <label>Poste {editId ? '' : '*'}</label>
                  {/* ═══════════════════════════════════════════════════════════
                      ② LE POSTE SORT DU FORMULAIRE DE MODIFICATION  *(C2b)*
                      ═══════════════════════════════════════════════════════════
                      🎯 DÉCISION DE DOM, 13/10/2026. À la CRÉATION le champ est
                         un choix — c'est la première valeur de la carrière. Sur
                         un salarié EXISTANT il devient une LECTURE, et le geste
                         qui le change est à côté.
                      ⚠️ GRISER SANS DIRE POURQUOI AURAIT ÉTÉ PIRE QU'UN REFUS :
                         la RH aurait cru à une panne de droits. Le champ dit ce
                         qu'il est devenu, et nomme le geste (règle 62).
                      ⛔ ET LE CHAMP N'EST PAS SEULEMENT GRISÉ : `handleSubmit`
                         retire `poste` de la charge utile en modification. *Un
                         champ désactivé à l'écran reste envoyé par un formulaire
                         — et le serveur refuserait tout l'enregistrement.* */}
                  {editId ? (
                    <>
                      <div className="gss-input" style={{
                        background: 'var(--gss-bg-muted, #f5f5f5)', display: 'flex',
                        alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem',
                      }}>
                        {/* ⓘ Même raison qu'à l'historique : le titre déclaré par le
                             client s'affiche tel qu'il l'a écrit. */}
                        <span style={postesRef.some((p) => p.code === form.poste)
                          ? undefined : { textTransform: 'capitalize' }}>
                          {postesRef.find((p) => p.code === form.poste)?.titre
                            || form.poste?.replace(/_/g, ' ')
                            || 'aucun poste'}
                        </span>
                        <button type="button" className="gss-btn" style={{ fontSize: 12 }}
                          onClick={() => ouvrirEvoluer({
                            id: editId, nom: form.nom, prenom: form.prenom,
                          })}>
                          Faire évoluer
                        </button>
                      </div>
                      <small style={{ color: 'var(--gss-text-muted, #777)' }}>
                        Le poste et la population sont datés : ils se changent par
                        « Faire évoluer », avec une date d’effet et un motif, et
                        l’historique se conserve.
                      </small>
                    </>
                  ) : (
                  <select className="gss-input" required value={form.poste} onChange={e => setField('poste', e.target.value)}>
                    <option value="">— choisir un poste —</option>
                    {postesRef.map(p => <option key={p.code} value={p.code}>{p.titre}</option>)}
                  </select>
                  )}
                  {/* ⚠️ TEXTE ALIGNÉ LE 14/09/2026. Cet écran disait déjà le vide,
                      mais dans SA propre formulation. Deux énoncés pour une
                      notion divergent toujours, et le jour où l'on corrige l'un,
                      l'autre survit — « 2 mois offerts » vient de traverser
                      trois lots pour cette raison exacte.
                      ⓘ L'ancienne phrase n'est pas recopiée ici : un commentaire
                        qui cite le texte qu'il remplace le fait remonter à chaque
                        recherche, et on croit la correction inachevée. Elle est
                        conservée une fois, dans l'en-tête du fichier de libellés.
                      ⓘ Le nouveau texte dit en plus QUI fait ce travail, sans
                        parler du lecteur ni se ramifier par rôle (règle 26). */}
                  {!editId && postesRef.length === 0 && (
                    <small style={{ color: '#B45309' }}>{AUCUN_POSTE}</small>
                  )}
                </div>
                <div className="gss-field">
                  <label>Salaire de base (FCFA)</label>
                  <input className="gss-input" type="number" min="0" value={form.salaire_base} onChange={e => setField('salaire_base', e.target.value)} />
                </div>

                {/* ⚠️ NUMERO D'AFFILIE CNSS — attribue par la CAISSE. Sans lui,
                    la declaration REFUSE en nommant l'agent : elle ne l'omet
                    pas et ne fabrique aucun numero. */}
                <div className="gss-field">
                  <label>Numero d'affilie CNSS</label>
                  <input
                    className="gss-input" type="text"
                    value={form.numero_cnss}
                    onChange={e => setField('numero_cnss', e.target.value)}
                  />
                  {!form.numero_cnss && (
                    <small style={{ color: '#555' }}>
                      Sans ce numero, cet agent ne peut pas figurer sur la declaration CNSS.
                    </small>
                  )}
                </div>

                {/* ── CATEGORIE PROFESSIONNELLE ─────────────────────────────
                    Elle commande le SALAIRE PLANCHER et conditionne la
                    generation du contrat de travail. Trois etats, trois
                    affichages — jamais un tiret ambigu. */}
                {classifActuelle ? (
                  <div className="gss-field" style={{ gridColumn: '1 / -1' }}>
                    <label>Categorie professionnelle</label>
                    <div className="gss-input" style={{ background: '#f5f5f5', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <strong>{classifActuelle.code}</strong>
                      <span style={{ color: '#555' }}>{classifActuelle.libelle}</span>
                      <span style={{ color: '#555', marginLeft: 'auto', fontSize: 12 }}>
                        depuis le {formatDate(classifActuelle.date_debut)}
                        {classifActuelle.plancher != null
                          && ` · plancher ${Number(classifActuelle.plancher).toLocaleString('fr-FR')} F`}
                      </span>
                    </div>
                    {/* ⚠️ RIEN DE PLUS. Le champ dit le cran et sa date, un point.
                        Il ne mentionne NI le chantier « promotion », NI un
                        « bientot disponible » — REGLE 26 : le produit ne
                        documente pas ses manques a l'ecran. */}
                  </div>
                ) : (
                  <>
                    <div className="gss-field">
                      <label>Categorie professionnelle</label>
                      <select
                        className="gss-input"
                        value={form.cran}
                        onChange={(e) => {
                          const c = crans.find(x => x.code === e.target.value);
                          setForm(f => ({ ...f, cran: e.target.value, cran_origine: c ? c.origine : '' }));
                        }}
                      >
                        {/* ⚠️ L'option vide est le DEFAUT et le reste : aucun cran
                            n'est pre-selectionne (regle 41). */}
                        <option value="">— non classe —</option>
                        {crans.map(c => (
                          <option key={c.origine + c.code} value={c.code}>
                            {c.code} — {c.libelle}
                            {c.plancher != null ? ` (${Number(c.plancher).toLocaleString('fr-FR')} F)` : ''}
                          </option>
                        ))}
                      </select>
                      {cransMessage && <small style={{ color: '#B45309' }}>{cransMessage}</small>}
                      {!cransMessage && !form.cran && (
                        <small style={{ color: '#555' }}>
                          Sans categorie, aucun contrat de travail ne peut etre genere pour cet agent.
                        </small>
                      )}
                    </div>
                    {form.cran && (
                      <div className="gss-field">
                        <label>Categorie applicable depuis le *</label>
                        <input
                          className="gss-input" type="date" required
                          value={form.cran_date}
                          onChange={e => setField('cran_date', e.target.value)}
                        />
                        <small style={{ color: '#555' }}>
                          Propose : la date d'embauche. Une categorie qui demarre apres l'embauche
                          laisse la periode precedente sans base salariale.
                        </small>
                      </div>
                    )}
                  </>
                )}

                {/* 🔴 LE BLOC « FORMATIONS & NOTES » A ETE RETIRE — 16/09/2026,
                  * file 43. Il etait saisi et PERDU : ni `formations` ni `notes`
                  * n est une colonne, et aucun appel ne les enregistrait.
                  *
                  * POURQUOI RETIRER PLUTOT QUE LEUR DONNER UNE DESTINATION :
                  * ⓘ UN VRAI SOUS-SYSTEME DE FORMATION EXISTE DEJA et FONCTIONNE
                  *   — tables `modules_formation`, `formations_agents`,
                  *   `parcours_formation`, routes `formationRoutes.js`, et
                  *   l ecran Operations s en sert (`/formations/assign`,
                  *   `/formations/valider/:id`). Ce bloc-ci etait un DOUBLON
                  *   MORT : cinq libelles en dur, sans rapport avec le
                  *   catalogue de modules du tenant. Lui donner une destination
                  *   aurait cree DEUX VERITES pour une meme notion — le defaut
                  *   qu on passe le reste du temps a defaire.
                  * ⓘ `notes` n a de destination NULLE PART. En inventer une ici
                  *   serait concevoir une fonction dans un lot de correction :
                  *   qui la lit, part-elle sur un document, est-ce une donnee
                  *   personnelle sous RLS ? Ces questions n ont pas ete posees.
                  * ⓘ RIEN N EST PERDU par ce retrait : rien n etait enregistre.
                  *   Le retrait ARRETE la perte, il ne la cause pas.
                  * ➡️ La formation d un agent se gere par le module Formations.
                  *   Un champ de notes reste a CONCEVOIR, pas a improviser. */}
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={saving}>
                  {saving ? 'Enregistrement...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg> {editId ? 'Modifier' : 'Creer'}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
          ② « FAIRE ÉVOLUER » — UN GESTE, UNE DATE, UN MOTIF  *(C2b, 13/10/2026)*
          ═══════════════════════════════════════════════════════════════════════
          🎯 MAQUETTE VALIDÉE PAR DOM : un seul formulaire pour le poste ET la
             population, pré-rempli des valeurs EN VIGUEUR, avec une date d'effet
             et un motif. L'historique est sous le formulaire : *on ne demande pas
             à quelqu'un de décider d'un changement sans lui montrer ce qui
             précède.*
          ⛔ AUCUN SALAIRE ICI. Une augmentation individuelle est une EXCEPTION de
             la grille (`agent_id` renseigné) : la mêler à ce geste risquerait de
             toucher la grille de tous les salariés du même cran. */}
      {evoluer && (
        <div className="gss-modal-overlay" onClick={() => setEvoluer(null)}>
          {/* ⓘ `gss-modal-lg` : la modale porte l'historique de carrière, et en
               largeur ordinaire la colonne « Motif » était coupée — vu à la revue
               du 13/10. *Un historique qu'on ne peut pas lire ne sert à rien.* */}
          <div className="gss-modal gss-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>Faire évoluer — {evoluer.prenom} {evoluer.nom}</h2>
              <div className="gss-modal-subtitle">
                Poste et population, à une date d’effet
              </div>
              <button className="gss-modal-close" onClick={() => setEvoluer(null)}>&times;</button>
            </div>

            {evoluerErreur && (
              <div style={{ padding: '0.5rem 1.5rem', color: '#e53935', fontSize: '0.9rem' }}>
                {evoluerErreur}
              </div>
            )}
            {/* ⚠️ LES ALERTES SE VOIENT ET NE BLOQUENT RIEN (Dom, 13/10) : le
                changement EST enregistré ; c'est la conséquence sur la paie du
                mois qui demande une décision humaine. */}
            {evoluerAlertes.length > 0 && (
              <div style={{
                margin: '0 1.5rem', border: '1px solid #e0c08a', background: '#fdf6e3',
                borderRadius: 6, padding: '0.6rem 0.9rem', fontSize: 13, color: '#B45309',
              }}>
                <strong>Enregistré.</strong>
                {evoluerAlertes.map((a) => <div key={a.code} style={{ marginTop: 4 }}>{a.message}</div>)}
              </div>
            )}

            <form onSubmit={soumettreEvoluer}>
              <div className="gss-form-grid" style={{ padding: '1.5rem' }}>
                {/* ⛔ « AUCUNE SITUATION REPRISE » SE DIT. Un formulaire vide
                    ferait croire à un salarié sans poste, alors que le produit
                    ne sait simplement rien de lui — et la paie le refusera en le
                    nommant. */}
                {evoluer.carriere && !evoluer.carriere.en_vigueur && (
                  <div style={{ gridColumn: '1 / -1', color: '#B45309', fontSize: 13 }}>
                    Aucune situation n’est enregistrée pour ce salarié. La première que
                    vous posez ici ouvrira sa carrière.
                  </div>
                )}
                {evoluer.carriere && evoluer.carriere.en_vigueur && (
                  <div style={{ gridColumn: '1 / -1', fontSize: 13, color: 'var(--gss-text-muted, #777)' }}>
                    En vigueur depuis{' '}
                    {evoluer.carriere.en_vigueur.anteriorite_indeterminee
                      ? 'une date inconnue (antériorité indéterminée)'
                      : `le ${formatDate(evoluer.carriere.en_vigueur.date_effet)}`}.
                  </div>
                )}

                <div className="gss-field">
                  <label>Poste</label>
                  <select className="gss-input" value={evoluerForm.poste}
                    onChange={(e) => setEvoluerForm((f) => ({ ...f, poste: e.target.value }))}>
                    <option value="">— aucun poste —</option>
                    {postesRef.map((p) => <option key={p.code} value={p.code}>{p.titre}</option>)}
                  </select>
                  {postesRef.length === 0 && (
                    <small style={{ color: '#B45309' }}>{AUCUN_POSTE}</small>
                  )}
                </div>
                <div className="gss-field">
                  <label>Population</label>
                  <select className="gss-input" value={evoluerForm.population_id}
                    onChange={(e) => setEvoluerForm((f) => ({ ...f, population_id: e.target.value }))}>
                    <option value="">— aucune population —</option>
                    {populationsRef.map((p) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                  </select>
                  {populationsRef.length === 0 && (
                    <small style={{ color: '#B45309' }}>
                      Aucune population n’est déclarée. Déclarez-en une dans
                      « Paramétrage de la paie ».
                    </small>
                  )}
                </div>
                <div className="gss-field">
                  <label>À partir du *</label>
                  <input className="gss-input" type="date" required value={evoluerForm.date_effet}
                    onChange={(e) => setEvoluerForm((f) => ({ ...f, date_effet: e.target.value }))} />
                </div>
                <div className="gss-field">
                  <label>Motif *</label>
                  <input className="gss-input" required value={evoluerForm.motif}
                    placeholder="ex. : promotion au poste de chef de poste"
                    onChange={(e) => setEvoluerForm((f) => ({ ...f, motif: e.target.value }))} />
                </div>

                {/* ── L'HISTORIQUE, SOUS LE FORMULAIRE ─────────────────────────
                    ⚠️ LES LIGNES ANNULÉES Y RESTENT, ET ELLES LE DISENT. Les
                       masquer effacerait de la carrière une décision prise puis
                       retirée — avec son motif d'annulation, qui est justement
                       ce qu'on vient relire. */}
                {evoluer.carriere && evoluer.carriere.historique.length > 0 && (
                  <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                      Historique de carrière
                    </div>
                    <div className="gss-table-wrap">
                      <table className="gss-table">
                        <thead>
                          <tr><th>À partir du</th><th>Poste</th><th>Population</th>
                            <th>Motif</th><th>Origine</th></tr>
                        </thead>
                        <tbody>
                          {evoluer.carriere.historique.map((s) => (
                            <tr key={s.id} style={s.annulee
                              ? { textDecoration: 'line-through', color: 'var(--gss-text-muted, #999)' }
                              : undefined}>
                              <td>{s.anteriorite_indeterminee
                                ? 'antériorité indéterminée'
                                : formatDate(s.date_effet)}</td>
                              {/* ⚠️ LA MISE EN CAPITALES NE S'APPLIQUE QU'AU CODE BRUT.
                                   Vue à la revue du 13/10 : « Chef de poste (revue) »,
                                   le titre que le client a écrit lui-même, s'affichait
                                   « Chef De Poste (Revue) ». *Le `capitalize` servait à
                                   rendre lisible un code technique (`agent_jour`) ; appliqué
                                   au libellé du tenant, il le déforme.* */}
                              <td style={s.poste_libelle ? undefined : { textTransform: 'capitalize' }}>
                                {s.poste_libelle || s.poste?.replace(/_/g, ' ') || '—'}
                              </td>
                              <td>{s.population_libelle || '—'}</td>
                              <td style={{ fontSize: 12 }}>
                                {s.motif}
                                {s.annulee && (
                                  <div style={{ color: '#B45309' }}>
                                    Annulée : {s.motif_annulation}
                                  </div>
                                )}
                              </td>
                              <td style={{ fontSize: 12 }}>
                                {s.origine_ecriture === 'reprise'
                                  ? 'reprise'
                                  : (s.acteur || 'saisie')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setEvoluer(null)}>
                  Fermer
                </button>
                <button type="submit" className="gss-btn-deploy"
                  disabled={saving || !evoluerForm.date_effet || !evoluerForm.motif.trim()}
                  title={!evoluerForm.date_effet ? 'Indiquer la date d’effet.'
                    : (!evoluerForm.motif.trim() ? 'Indiquer le motif.' : '')}>
                  {saving ? 'Enregistrement…' : 'Enregistrer l’évolution'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
